<template>
  <section class="team-goals" :class="{ 'team-goals--loading': loading }">
    <header class="team-goals__hero">
      <div class="hero-copy">
        <div class="hero-badges">
          <span class="pilot-badge"><v-icon x-small color="white">mdi-flask-outline</v-icon> PILOT</span>
          <span class="hero-context">Geschäftsleitungs-Vorschau</span>
        </div>
        <h1>Team &amp; Ziele</h1>
        <p>Trainerleistung, Monatsziele und Entwicklung in einer gemeinsamen, ruhigen Arbeitsfläche.</p>
        <div class="hero-meta">
          <span><v-icon small>mdi-calendar-month-outline</v-icon>{{ rangeLabel }}</span>
          <span><v-icon small>mdi-office-building-marker-outline</v-icon>{{ studioLabel }}</span>
          <span v-if="lastLoadedAt"><v-icon small>mdi-database-check-outline</v-icon>Stand {{ lastLoadedAt }}</span>
        </div>
      </div>
      <div class="hero-actions">
        <v-btn class="hero-refresh" depressed :loading="loading" @click="loadData(true)"> <v-icon left>mdi-refresh</v-icon>Aktualisieren </v-btn>
      </div>
    </header>

    <div class="pilot-notice">
      <v-icon color="#60a5fa">mdi-information-outline</v-icon>
      <div>
        <strong>Zusätzliche Pilotstatistik</strong>
        <span>Die bestehenden drei Trainerstatistiken bleiben während der Präsentations- und Prüfphase unverändert verfügbar.</span>
      </div>
    </div>

    <div v-if="loading && !hasData" class="loading-shell">
      <div v-for="index in 4" :key="`skeleton-${index}`" class="skeleton-card"></div>
      <div class="skeleton-panel"></div>
    </div>

    <div v-else-if="error && !hasData" class="empty-state empty-state--error">
      <v-icon size="48" color="#fb7185">mdi-alert-circle-outline</v-icon>
      <h2>Teamdaten konnten nicht geladen werden</h2>
      <p>{{ error }}</p>
      <v-btn depressed color="primary" @click="loadData(true)">Erneut versuchen</v-btn>
    </div>

    <template v-else>
      <div class="kpi-grid">
        <article v-for="card in kpiCards" :key="card.key" class="kpi-card" :style="{ '--kpi-color': card.color }">
          <div class="kpi-card__top">
            <span class="kpi-icon"
              ><v-icon :color="card.color">{{ card.icon }}</v-icon></span
            >
            <span v-if="card.badge" class="kpi-badge">{{ card.badge }}</span>
          </div>
          <strong>{{ card.value }}</strong>
          <span>{{ card.label }}</span>
          <small>{{ card.hint }}</small>
        </article>
      </div>

      <nav class="view-switch" aria-label="Ansicht auswählen">
        <button
          v-for="view in views"
          :key="view.key"
          type="button"
          :class="{ 'view-switch__button--active': activeView === view.key }"
          @click="activeView = view.key">
          <v-icon small>{{ view.icon }}</v-icon>
          {{ view.label }}
        </button>
      </nav>

      <div class="filter-bar">
        <v-text-field
          v-model.trim="search"
          dark
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          label="Trainer suchen"
          class="filter-search" />
        <v-select
          v-model="selectedMetricKey"
          :items="metricOptions"
          item-text="label"
          item-value="key"
          dark
          dense
          outlined
          hide-details
          label="Kennzahl"
          class="filter-metric" />
        <v-switch v-model="onlyAttention" dark inset hide-details color="#f59e0b" label="Nur Handlungsbedarf" />
      </div>

      <section v-if="activeView === 'overview'" class="view-content">
        <div class="overview-grid">
          <article class="panel panel--wide">
            <div class="panel-heading">
              <div>
                <span class="eyebrow">ZIELBILD</span>
                <h2>Leistung auf einen Blick</h2>
                <p>Ist und Ziel aller Kennzahlen im ausgewählten Monat.</p>
              </div>
              <span class="selection-chip">{{ filteredTrainers.length }} Mitarbeitende</span>
            </div>
            <div class="metric-summary-list">
              <button
                v-for="metric in aggregateMetrics"
                :key="metric.key"
                type="button"
                class="metric-summary"
                @click="selectMetricAndOpenTeam(metric.key)">
                <span class="metric-summary__icon" :style="{ backgroundColor: `${metric.color}22` }">
                  <v-icon :color="metric.color" small>{{ metric.icon }}</v-icon>
                </span>
                <span class="metric-summary__copy">
                  <strong>{{ metric.label }}</strong>
                  <small>{{ formatNumber(metric.actual) }} Ist · {{ metric.hasTarget ? `${formatNumber(metric.target)} Ziel` : "kein Ziel" }}</small>
                </span>
                <span class="metric-summary__progress">
                  <span><i :style="{ width: `${Math.min(metric.attainment, 100)}%`, backgroundColor: metric.color }"></i></span>
                  <strong v-if="metric.hasTarget">{{ Math.round(metric.attainment) }}%</strong>
                  <strong v-else>–</strong>
                </span>
                <v-icon small color="#64748b">mdi-chevron-right</v-icon>
              </button>
            </div>
          </article>

          <article class="panel">
            <div class="panel-heading">
              <div>
                <span class="eyebrow eyebrow--warning">PRIORITÄTEN</span>
                <h2>Wo lohnt sich ein Gespräch?</h2>
              </div>
            </div>
            <div v-if="attentionTrainers.length" class="attention-list">
              <button
                v-for="trainer in attentionTrainers.slice(0, 6)"
                :key="trainer._id"
                type="button"
                class="attention-person"
                @click="openTrainerDetails(trainer)">
                <span class="person-avatar person-avatar--small">
                  <img :src="profileImage(trainer)" :alt="trainer.fullName" />
                </span>
                <span>
                  <strong>{{ trainer.fullName }}</strong>
                  <small>{{ attentionReason(trainer) }}</small>
                </span>
                <v-icon small>mdi-chevron-right</v-icon>
              </button>
            </div>
            <div v-else class="panel-empty">
              <v-icon color="#34d399" size="38">mdi-check-circle-outline</v-icon>
              <strong>Kein akuter Handlungsbedarf</strong>
              <span>Alle hinterlegten Ziele liegen aktuell im grünen Bereich.</span>
            </div>
          </article>

          <article class="panel">
            <div class="panel-heading">
              <div>
                <span class="eyebrow eyebrow--blue">DATENHERKUNFT</span>
                <h2>Termine Ist</h2>
                <p>Bis 31.07.2026 gilt der fest hinterlegte Ist-Wert. Ab 01.08.2026 zählt ausschliesslich der WorkCalendar.</p>
              </div>
            </div>
            <div class="source-total">
              <strong>{{ formatNumber(appointmentTotal) }}</strong>
              <span>nicht stornierte Termine</span>
            </div>
            <div class="source-list">
              <div v-for="source in appointmentSourceCards" :key="source.key">
                <span class="source-dot" :style="{ backgroundColor: source.color }"></span>
                <span
                  ><strong>{{ source.count }}</strong
                  >{{ source.label }}</span
                >
              </div>
            </div>
            <div class="status-strip">
              <div v-for="status in appointmentStatusCards" :key="status.key">
                <strong>{{ status.value }}</strong
                ><span>{{ status.label }}</span>
              </div>
            </div>
            <small class="source-footnote">
              Die Statusaufteilung enthält nur WorkCalendar-Daten ab 01.08.2026. Stornierte und gelöschte Termine werden nicht gezählt; No-Shows
              bleiben gemäss Pilotdefinition enthalten.
            </small>
          </article>
        </div>
      </section>

      <section v-else-if="activeView === 'team'" class="view-content">
        <article class="panel panel--table">
          <div class="panel-heading panel-heading--table">
            <div>
              <span class="eyebrow">TEAMANSICHT</span>
              <h2>{{ selectedMetric.label }}</h2>
              <p>Sortiert nach Zielerreichung – Details und alle Kennzahlen öffnen sich pro Person.</p>
            </div>
            <div class="legend">
              <span><i class="legend-dot legend-dot--good"></i>Im Ziel</span>
              <span><i class="legend-dot legend-dot--watch"></i>Beobachten</span>
              <span><i class="legend-dot legend-dot--attention"></i>Handlungsbedarf</span>
            </div>
          </div>
          <v-data-table
            dark
            :headers="tableHeaders"
            :items="teamRows"
            :items-per-page="12"
            :mobile-breakpoint="900"
            no-data-text="Keine Mitarbeitenden für diese Auswahl gefunden."
            class="team-table">
            <template v-slot:item.trainer="{ item }">
              <div class="trainer-cell">
                <span class="person-avatar"><img :src="profileImage(item.trainer)" :alt="item.trainer.fullName" /></span>
                <span>
                  <strong>{{ item.trainer.fullName }}</strong>
                  <small>{{ trainerStudioLabel(item.trainer) }}</small>
                </span>
              </div>
            </template>
            <template v-slot:item.actual="{ item }">
              <strong class="table-number">{{ formatNumber(item.actual) }}</strong>
            </template>
            <template v-slot:item.target="{ item }">
              <span class="table-target">{{ item.hasTarget ? formatNumber(item.target) : "Nicht hinterlegt" }}</span>
            </template>
            <template v-slot:item.progress="{ item }">
              <div class="table-progress">
                <span><i :class="`tone--${item.tone}`" :style="{ width: `${Math.min(item.attainment, 100)}%` }"></i></span>
                <strong>{{ item.hasTarget ? `${Math.round(item.attainment)}%` : "–" }}</strong>
              </div>
            </template>
            <template v-slot:item.appointments="{ item }">
              <div class="appointment-cell">
                <strong>{{ appointmentActual(item.trainer) }}</strong>
                <small :class="`source-text--${appointmentSource(item.trainer)}`">{{ appointmentSourceLabel(item.trainer) }}</small>
              </div>
            </template>
            <template v-slot:item.status="{ item }">
              <span class="status-pill" :class="`status-pill--${item.tone}`">{{ toneLabel(item.tone) }}</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <div class="row-actions">
                <v-btn icon small @click="openTrainerDetails(item.trainer)"><v-icon small>mdi-eye-outline</v-icon></v-btn>
                <v-btn v-if="canEditGoals" icon small color="primary" @click="openTargetDialog(item.trainer)">
                  <v-icon small>mdi-target-account</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </article>
      </section>

      <section v-else class="view-content">
        <div class="analysis-grid">
          <article class="panel panel--wide">
            <div class="panel-heading">
              <div>
                <span class="eyebrow">KENNZAHLEN-MATRIX</span>
                <h2>Teamstärken und offene Ziele</h2>
                <p>Die Werte beziehen sich auf die aktuell ausgewählten Mitarbeitenden – nicht auf eine künstliche Center-Zuordnung.</p>
              </div>
            </div>
            <div class="analysis-metrics">
              <button v-for="metric in aggregateMetrics" :key="`analysis-${metric.key}`" type="button" @click="selectMetricAndOpenTeam(metric.key)">
                <span
                  ><v-icon :color="metric.color">{{ metric.icon }}</v-icon
                  >{{ metric.label }}</span
                >
                <strong>{{ formatNumber(metric.actual) }}</strong>
                <small v-if="metric.hasTarget">von {{ formatNumber(metric.target) }} · {{ Math.round(metric.attainment) }}%</small>
                <small v-else>Noch kein Teamziel hinterlegt</small>
                <i><b :style="{ width: `${Math.min(metric.attainment, 100)}%`, backgroundColor: metric.color }"></b></i>
              </button>
            </div>
          </article>

          <article class="panel">
            <div class="panel-heading">
              <div>
                <span class="eyebrow eyebrow--blue">TEAMSTRUKTUR</span>
                <h2>Center-Zugehörigkeit</h2>
                <p>Multi-Center-Mitarbeitende können mehreren Teams zugeordnet sein.</p>
              </div>
            </div>
            <div class="center-memberships">
              <div v-for="center in centerMemberships" :key="center.id">
                <span
                  ><strong>{{ center.name }}</strong
                  ><small>{{ center.count }} Mitarbeitende</small></span
                >
                <span class="membership-bar"><i :style="{ width: `${center.percentage}%` }"></i></span>
              </div>
            </div>
          </article>

          <article class="panel">
            <div class="panel-heading">
              <div>
                <span class="eyebrow eyebrow--warning">ZIELQUALITÄT</span>
                <h2>Vollständigkeit</h2>
              </div>
            </div>
            <div class="quality-ring" :style="{ '--quality': `${goalCoverage}%` }">
              <div>
                <strong>{{ goalCoverage }}%</strong><span>Ziele gepflegt</span>
              </div>
            </div>
            <div class="quality-copy">
              <strong>{{ trainersWithGoals }} von {{ filteredTrainers.length }}</strong>
              <span>Mitarbeitenden haben mindestens ein Monatsziel.</span>
            </div>
          </article>
        </div>
      </section>
    </template>

    <v-dialog v-model="detailDialog" max-width="1040" content-class="team-goals-dialog">
      <v-card v-if="selectedTrainer" class="detail-card" dark>
        <div class="detail-hero">
          <span class="person-avatar person-avatar--large"><img :src="profileImage(selectedTrainer)" :alt="selectedTrainer.fullName" /></span>
          <div>
            <span class="eyebrow">MITARBEITER-DETAIL</span>
            <h2>{{ selectedTrainer.fullName }}</h2>
            <p>{{ trainerStudioLabel(selectedTrainer) }} · {{ selectedTrainer.percentage || 0 }} Stellenprozent</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn v-if="canEditGoals" depressed color="primary" @click="openTargetDialog(selectedTrainer)">
            <v-icon left>mdi-target-account</v-icon>Ziele bearbeiten
          </v-btn>
          <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="detail-body">
          <div class="detail-metrics">
            <article v-for="metric in selectedTrainerMetrics" :key="`detail-${metric.key}`">
              <span class="detail-metric__icon" :style="{ backgroundColor: `${metric.color}22` }">
                <v-icon :color="metric.color">{{ metric.icon }}</v-icon>
              </span>
              <div>
                <small>{{ metric.label }}</small>
                <strong
                  >{{ formatNumber(metric.actual) }} <em v-if="metric.hasTarget">/ {{ formatNumber(metric.target) }}</em></strong
                >
              </div>
              <span class="detail-progress"><i :class="`tone--${metric.tone}`" :style="{ width: `${Math.min(metric.attainment, 100)}%` }"></i></span>
              <b>{{ metric.hasTarget ? `${Math.round(metric.attainment)}%` : "Kein Ziel" }}</b>
            </article>
          </div>

          <div class="detail-insights">
            <article>
              <span class="eyebrow">QUALITÄT &amp; FEEDBACK</span>
              <h3>Kundenbewertung</h3>
              <div class="rating-row">
                <span>Trainerbetreuung</span>
                <v-rating
                  :value="Number(selectedTrainer.tp_question_1 || 0)"
                  color="amber"
                  background-color="blue-grey darken-2"
                  half-increments
                  dense
                  readonly />
                <strong>{{ ratingLabel(selectedTrainer.tp_question_1) }}</strong>
              </div>
              <div class="rating-row">
                <span>Trainingsplan</span>
                <v-rating
                  :value="Number(selectedTrainer.tp_question_2 || 0)"
                  color="amber"
                  background-color="blue-grey darken-2"
                  half-increments
                  dense
                  readonly />
                <strong>{{ ratingLabel(selectedTrainer.tp_question_2) }}</strong>
              </div>
            </article>

            <article>
              <span class="eyebrow eyebrow--blue">ZUSATZDETAILS</span>
              <h3>Verkauf &amp; Betreuung</h3>
              <div class="detail-facts">
                <div>
                  <strong>{{ formatNumber(selectedTrainer.avgPackages) }}</strong
                  ><span>Ø Pakete pro Vertrag</span>
                </div>
                <div>
                  <strong>{{ formatNumber(selectedTrainer.quote) }}%</strong><span>Abschlussquote</span>
                </div>
                <div>
                  <strong>{{ formatNumber(selectedTrainer.interactions && selectedTrainer.interactions.totalInteractions) }}</strong
                  ><span>Interaktionen</span>
                </div>
                <div>
                  <strong>{{ formatNumber(selectedTrainer.contractCountTotal) }}</strong
                  ><span>Neue Vertragskunden</span>
                </div>
              </div>
            </article>

            <article v-if="interactionLocations(selectedTrainer).length" class="detail-insights__wide">
              <span class="eyebrow eyebrow--warning">KUNDENKONTAKTE</span>
              <h3>Interaktionen nach Ort</h3>
              <div class="location-chips">
                <span v-for="location in interactionLocations(selectedTrainer)" :key="location.name">
                  {{ location.name }} <strong>{{ location.count }}</strong>
                </span>
              </div>
            </article>
          </div>

          <div class="appointment-detail">
            <div class="appointment-detail__heading">
              <div>
                <span class="eyebrow eyebrow--blue">TERMIN-IST</span>
                <h3>{{ appointmentActual(selectedTrainer) }} nicht stornierte Termine</h3>
              </div>
              <span class="source-pill" :class="`source-pill--${appointmentSource(selectedTrainer)}`">
                {{ appointmentSourceLabel(selectedTrainer) }}
              </span>
            </div>
            <div class="appointment-detail__statuses">
              <div v-for="status in trainerAppointmentStatuses(selectedTrainer)" :key="`trainer-${status.key}`">
                <v-icon :color="status.color">{{ status.icon }}</v-icon>
                <strong>{{ status.value }}</strong>
                <span>{{ status.label }}</span>
              </div>
            </div>
            <p v-if="appointmentSource(selectedTrainer) === 'manual'">
              Bis 31.07.2026 zählt ausschliesslich der fest hinterlegte Ist-Wert. WorkCalendar-Daten werden dafür nicht rückwirkend verwendet.
            </p>
            <p v-else-if="appointmentSource(selectedTrainer) === 'manual_missing'">
              Für diesen Zeitraum vor August 2026 fehlt ein fest hinterlegter Ist-Wert. Er wird nicht durch rückwirkende WorkCalendar-Daten ersetzt.
            </p>
            <p v-else-if="appointmentSource(selectedTrainer) === 'mixed'">
              Der Zeitraum kombiniert feste historische Ist-Werte bis 31.07.2026 mit WorkCalendar-Terminen ab 01.08.2026.
            </p>
            <p v-else-if="appointmentSource(selectedTrainer) === 'mixed_missing'">
              Der Zeitraum kombiniert beide Datenquellen, aber mindestens einem Monat vor August 2026 fehlt der fest hinterlegte Ist-Wert.
            </p>
            <p v-else>Ab 01.08.2026 zählen alle WorkCalendar-Termine ausser stornierten, gelöschten und reinen MA-Info-Einträgen.</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="targetDialog" max-width="760" content-class="team-goals-dialog">
      <v-card v-if="targetTrainer" class="target-card" dark>
        <v-card-title>
          <div>
            <span class="eyebrow">MONATSZIELE</span>
            <h2>{{ targetTrainer.fullName }}</h2>
            <p>{{ goalMonthLabel }}</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="targetDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="target-form">
            <v-text-field
              v-for="field in targetFields"
              :key="field.key"
              v-model.number="targetDraft[field.key]"
              :label="field.label"
              :prepend-inner-icon="field.icon"
              type="number"
              min="0"
              outlined
              dense
              dark />
          </div>
          <div class="actual-readonly">
            <span><v-icon color="#60a5fa">mdi-calendar-check-outline</v-icon></span>
            <div>
              <small>Termine Ist · nicht editierbar</small>
              <strong>{{ appointmentActual(targetTrainer) }}</strong>
              <p>{{ appointmentSourceLabel(targetTrainer) }} – bis 31.07.2026 historisch fix, ab 01.08.2026 automatisch aus dem WorkCalendar.</p>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="targetDialog = false">Abbrechen</v-btn>
          <v-btn depressed color="primary" :loading="savingTargets" @click="saveTargets">
            <v-icon left>mdi-content-save-outline</v-icon>Ziele speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div v-if="loading && hasData" class="refresh-overlay"><v-progress-circular indeterminate color="primary" /></div>
  </section>
</template>

<script>
import api from "../../../api"
import { mapState } from "vuex"
import userService from "@/services/userService.js"

const METRICS = [
  { key: "contracts", label: "Verträge", actualKey: "contracts", targetKey: "contracts", icon: "mdi-file-sign", color: "#38bdf8", group: "Verkauf" },
  {
    key: "packages",
    label: "Pakete",
    actualKey: "packageCount",
    targetKey: "packages",
    icon: "mdi-package-variant-closed",
    color: "#818cf8",
    group: "Verkauf",
  },
  { key: "upgrades", label: "Upgrades", actualKey: "upgrades", targetKey: "upgrades", icon: "mdi-trending-up", color: "#c084fc", group: "Verkauf" },
  {
    key: "anamneses",
    label: "Anamnesen",
    actualKey: "medicalHistory",
    targetKey: "anamneses",
    icon: "mdi-clipboard-pulse-outline",
    color: "#2dd4bf",
    group: "Kundenbetreuung",
  },
  {
    key: "workouts",
    label: "Trainingspläne",
    actualKey: "workouts",
    targetKey: "workouts",
    icon: "mdi-dumbbell",
    color: "#34d399",
    group: "Kundenbetreuung",
  },
  {
    key: "workoutsPerCustomer",
    label: "Unterschiedliche TP-Kunden",
    actualKey: "workoutsPerCustomer",
    targetKey: "workoutsPerCustomer",
    icon: "mdi-account-multiple-check-outline",
    color: "#22c55e",
    group: "Kundenbetreuung",
  },
  {
    key: "interactions",
    label: "Kundeninteraktionen",
    actualKey: "interactions.totalInteractions",
    targetKey: "interactions",
    icon: "mdi-account-heart-outline",
    color: "#f59e0b",
    group: "Kundenbetreuung",
  },
  {
    key: "appointments",
    label: "Termine",
    actualKey: "appointmentsActual",
    targetKey: "appointmentsTarget",
    icon: "mdi-calendar-check-outline",
    color: "#fb7185",
    group: "Termine",
  },
]

const TARGET_FIELDS = METRICS.map((metric) => ({ key: metric.targetKey, label: `${metric.label} Ziel`, icon: metric.icon })).filter(
  (field, index, fields) => fields.findIndex((candidate) => candidate.key === field.key) === index,
)
const TEAM_GOALS_CACHE = new Map()
const TEAM_GOALS_CACHE_TTL = 2 * 60 * 1000

function hasNumber(value) {
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value))
}

export default {
  name: "HomeDashboardTeamGoalsNew",
  props: {
    studios: { type: Array, default: () => [] },
    datePickerRange: { type: Array, default: () => [] },
  },
  data() {
    return {
      trainers: [],
      loading: false,
      error: "",
      lastLoadedAt: "",
      activeView: "overview",
      search: "",
      onlyAttention: false,
      selectedMetricKey: "contracts",
      detailDialog: false,
      selectedTrainer: null,
      targetDialog: false,
      targetTrainer: null,
      targetDraft: {},
      originalTargetGoals: {},
      savingTargets: false,
      requestSerial: 0,
      loadTimer: null,
      loadedRequestKey: "",
      views: [
        { key: "overview", label: "Überblick", icon: "mdi-view-dashboard-outline" },
        { key: "team", label: "Mitarbeitende", icon: "mdi-account-group-outline" },
        { key: "analysis", label: "Analyse", icon: "mdi-chart-box-outline" },
      ],
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    requestKey() {
      const studioIds = (this.studios || [])
        .map((studio) => String(studio?._id || studio || ""))
        .filter(Boolean)
        .sort()
      return `${studioIds.join(",")}|${(this.datePickerRange || []).join("|")}`
    },
    hasData() {
      return this.trainers.length > 0
    },
    rangeLabel() {
      if (!this.datePickerRange?.[0]) return "Kein Monat ausgewählt"
      return this.$moment(this.datePickerRange[0]).format("MMMM YYYY")
    },
    goalMonthLabel() {
      return this.datePickerRange?.[0] ? this.$moment(this.datePickerRange[0]).format("MMMM YYYY") : "Ausgewählter Monat"
    },
    studioLabel() {
      const names = (this.studios || []).map((studio) => studio?.name || studio?.title).filter(Boolean)
      if (!names.length) return "Kein Center ausgewählt"
      if (names.length === 1) return names[0]
      return `${names.length} Center ausgewählt`
    },
    filteredTrainers() {
      const query = String(this.search || "").toLocaleLowerCase("de-CH")
      return this.trainers
        .filter(
          (trainer) =>
            !String(trainer.fullName || "")
              .toLocaleLowerCase("de-CH")
              .includes("roodytrainer"),
        )
        .filter((trainer) => !query || `${trainer.fullName} ${this.trainerStudioLabel(trainer)}`.toLocaleLowerCase("de-CH").includes(query))
        .filter((trainer) => !this.onlyAttention || this.trainerNeedsAttention(trainer))
    },
    metricOptions() {
      return METRICS.map(({ key, label }) => ({ key, label }))
    },
    selectedMetric() {
      return METRICS.find((metric) => metric.key === this.selectedMetricKey) || METRICS[0]
    },
    aggregateMetrics() {
      return METRICS.map((metric) => {
        const actual = this.filteredTrainers.reduce((sum, trainer) => sum + this.metricActual(trainer, metric), 0)
        const targets = this.filteredTrainers
          .map((trainer) => this.metricTarget(trainer, metric))
          .filter((value) => hasNumber(value) && Number(value) > 0)
        const target = targets.reduce((sum, value) => sum + Number(value), 0)
        return { ...metric, actual, target, hasTarget: targets.length > 0, attainment: target > 0 ? (actual / target) * 100 : 0 }
      })
    },
    teamRows() {
      return this.filteredTrainers
        .map((trainer) => {
          const actual = this.metricActual(trainer, this.selectedMetric)
          const target = this.metricTarget(trainer, this.selectedMetric)
          const hasTarget = hasNumber(target) && Number(target) > 0
          const attainment = hasTarget ? (actual / Number(target)) * 100 : 0
          return { trainer, actual, target: Number(target || 0), hasTarget, attainment, tone: this.attainmentTone(attainment, hasTarget) }
        })
        .sort((a, b) => {
          if (a.hasTarget !== b.hasTarget) return a.hasTarget ? -1 : 1
          return a.attainment - b.attainment || String(a.trainer.fullName).localeCompare(String(b.trainer.fullName), "de")
        })
    },
    tableHeaders() {
      return [
        { text: "Mitarbeitende", value: "trainer", sortable: false },
        { text: "Ist", value: "actual", align: "end" },
        { text: "Ziel", value: "target", align: "end" },
        { text: "Zielerreichung", value: "progress", sortable: false, width: "220px" },
        { text: "Termine Ist", value: "appointments", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ]
    },
    canEditGoals() {
      return userService.hasRight("stat_admin", "stat_viewstudio")
    },
    attentionTrainers() {
      return this.filteredTrainers
        .filter((trainer) => this.trainerNeedsAttention(trainer))
        .sort((a, b) => this.trainerScore(a) - this.trainerScore(b))
    },
    appointmentTotal() {
      return this.filteredTrainers.reduce((sum, trainer) => sum + this.appointmentActual(trainer), 0)
    },
    appointmentSourceCards() {
      const counts = this.filteredTrainers.reduce(
        (total, trainer) => {
          const source = this.appointmentSource(trainer)
          const group = ["manual_missing", "mixed_missing"].includes(source) ? "missing" : source
          total[group] = (total[group] || 0) + 1
          return total
        },
        { workcalendar: 0, manual: 0, mixed: 0, missing: 0 },
      )
      return [
        { key: "workcalendar", label: "WorkCalendar ab August", count: counts.workcalendar, color: "#38bdf8" },
        { key: "manual", label: "Historisch fix", count: counts.manual, color: "#a78bfa" },
        { key: "mixed", label: "Gemischt", count: counts.mixed, color: "#f59e0b" },
        { key: "missing", label: "Historischer Ist-Wert fehlt", count: counts.missing, color: "#fb7185" },
      ]
    },
    appointmentStatusCards() {
      const totals = this.filteredTrainers.reduce(
        (sum, trainer) => {
          const breakdown = trainer.appointmentStatusBreakdown || {}
          Object.keys(sum).forEach((key) => (sum[key] += Number(breakdown[key] || 0)))
          return sum
        },
        { planned: 0, checkedin: 0, done: 0, noshow: 0 },
      )
      return [
        { key: "planned", label: "Geplant", value: totals.planned },
        { key: "checkedin", label: "Eingecheckt", value: totals.checkedin },
        { key: "done", label: "Durchgeführt", value: totals.done },
        { key: "noshow", label: "No-Show", value: totals.noshow },
      ]
    },
    averageAttainment() {
      const values = []
      this.filteredTrainers.forEach((trainer) => {
        METRICS.forEach((metric) => {
          const target = this.metricTarget(trainer, metric)
          if (hasNumber(target) && Number(target) > 0) values.push(Math.min((this.metricActual(trainer, metric) / Number(target)) * 100, 200))
        })
      })
      return values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : 0
    },
    trainersWithGoals() {
      return this.filteredTrainers.filter((trainer) => this.hasAnyGoal(trainer)).length
    },
    goalCoverage() {
      return this.filteredTrainers.length ? Math.round((this.trainersWithGoals / this.filteredTrainers.length) * 100) : 0
    },
    kpiCards() {
      return [
        {
          key: "team",
          label: "Mitarbeitende",
          value: this.filteredTrainers.length,
          hint: "in der aktuellen Auswahl",
          icon: "mdi-account-group-outline",
          color: "#38bdf8",
        },
        {
          key: "attainment",
          label: "Ø Zielerreichung",
          value: `${this.averageAttainment}%`,
          hint: "über alle gepflegten Ziele",
          icon: "mdi-target",
          color: "#34d399",
          badge: this.averageAttainment >= 100 ? "Im Ziel" : "Aktueller Stand",
        },
        {
          key: "attention",
          label: "Handlungsbedarf",
          value: this.attentionTrainers.length,
          hint: "unter 80% oder Ziel fehlt",
          icon: "mdi-account-alert-outline",
          color: "#f59e0b",
        },
        {
          key: "appointments",
          label: "Termine Ist",
          value: this.appointmentTotal,
          hint: "alle ausser storniert",
          icon: "mdi-calendar-check-outline",
          color: "#fb7185",
        },
      ]
    },
    selectedTrainerMetrics() {
      if (!this.selectedTrainer) return []
      return METRICS.map((metric) => this.enrichMetric(this.selectedTrainer, metric))
    },
    targetFields() {
      return TARGET_FIELDS
    },
    centerMemberships() {
      const counts = new Map()
      this.filteredTrainers.forEach((trainer) => {
        ;(Array.isArray(trainer.studio) ? trainer.studio : []).forEach((studioId) => {
          const id = String(studioId)
          counts.set(id, (counts.get(id) || 0) + 1)
        })
      })
      const max = Math.max(1, ...counts.values())
      return [...counts.entries()]
        .map(([id, count]) => ({ id, count, name: this.studioName(id), percentage: Math.round((count / max) * 100) }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "de"))
    },
  },
  watch: {
    requestKey: {
      immediate: true,
      handler() {
        this.queueLoadData()
      },
    },
  },
  beforeDestroy() {
    if (this.loadTimer) clearTimeout(this.loadTimer)
    this.requestSerial += 1
  },
  methods: {
    queueLoadData() {
      this.requestSerial += 1
      if (this.loadTimer) clearTimeout(this.loadTimer)
      this.loadTimer = setTimeout(() => {
        this.loadTimer = null
        this.loadData()
      }, 220)
    },
    async loadData(force = false) {
      if (this.loadTimer) {
        clearTimeout(this.loadTimer)
        this.loadTimer = null
      }
      const studioIds = (this.studios || []).map((studio) => studio?._id || studio).filter(Boolean)
      if (!studioIds.length || !this.datePickerRange?.[0] || !this.datePickerRange?.[1]) {
        this.trainers = []
        this.error = "Bitte mindestens ein Center und einen Monat auswählen."
        this.loading = false
        return
      }
      if (!force && this.loadedRequestKey === this.requestKey && this.hasData) return

      const cached = TEAM_GOALS_CACHE.get(this.requestKey)
      if (!force && cached && Date.now() - cached.createdAt < TEAM_GOALS_CACHE_TTL) {
        this.trainers = cached.trainers.map((trainer) => ({ ...trainer }))
        this.loadedRequestKey = this.requestKey
        this.lastLoadedAt = this.$moment(cached.createdAt).format("HH:mm")
        this.loading = false
        return
      }

      const requestId = ++this.requestSerial
      this.loading = true
      this.error = ""
      try {
        const result = await api.fetchTrainerDashboardInfos({
          studios: studioIds,
          range: this.datePickerRange,
          includeAppointmentFacts: true,
          compactTrainerStatistics: true,
        })
        if (requestId !== this.requestSerial) return
        this.trainers = Array.isArray(result) ? result : []
        TEAM_GOALS_CACHE.set(this.requestKey, { createdAt: Date.now(), trainers: this.trainers.map((trainer) => ({ ...trainer })) })
        this.loadedRequestKey = this.requestKey
        this.lastLoadedAt = this.$moment().format("HH:mm")
        if (this.selectedTrainer) {
          const refreshed = this.trainers.find((trainer) => String(trainer._id) === String(this.selectedTrainer._id))
          if (refreshed) this.selectedTrainer = refreshed
        }
      } catch (error) {
        if (requestId !== this.requestSerial) return
        this.error = error?.response?.data?.message || "Die neue Teamstatistik konnte nicht geladen werden."
        if (this.hasData && this.$toast) this.$toast.error(this.error)
        console.error("[TEAM_GOALS_NEW_LOAD]", error)
      } finally {
        if (requestId === this.requestSerial) this.loading = false
      }
    },
    nestedValue(object, path) {
      return String(path || "")
        .split(".")
        .reduce((value, key) => (value === null || value === undefined ? undefined : value[key]), object)
    },
    metricActual(trainer, metric) {
      if (metric.key === "appointments") return this.appointmentActual(trainer)
      const value = this.nestedValue(trainer, metric.actualKey)
      return hasNumber(value) ? Number(value) : 0
    },
    metricTarget(trainer, metric) {
      return trainer?.trainerGoal?.goals?.[metric.targetKey]
    },
    enrichMetric(trainer, metric) {
      const actual = this.metricActual(trainer, metric)
      const target = this.metricTarget(trainer, metric)
      const hasTarget = hasNumber(target) && Number(target) > 0
      const attainment = hasTarget ? (actual / Number(target)) * 100 : 0
      return { ...metric, actual, target: Number(target || 0), hasTarget, attainment, tone: this.attainmentTone(attainment, hasTarget) }
    },
    attainmentTone(attainment, hasTarget) {
      if (!hasTarget) return "missing"
      if (attainment >= 100) return "good"
      if (attainment >= 80) return "watch"
      return "attention"
    },
    toneLabel(tone) {
      return { good: "Im Ziel", watch: "Beobachten", attention: "Handlungsbedarf", missing: "Ziel fehlt" }[tone] || "Offen"
    },
    trainerNeedsAttention(trainer) {
      return METRICS.some((metric) => {
        const enriched = this.enrichMetric(trainer, metric)
        return enriched.tone === "attention" || enriched.tone === "missing"
      })
    },
    trainerScore(trainer) {
      const configured = METRICS.map((metric) => this.enrichMetric(trainer, metric)).filter((metric) => metric.hasTarget)
      if (!configured.length) return -1
      return configured.reduce((sum, metric) => sum + Math.min(metric.attainment, 200), 0) / configured.length
    },
    attentionReason(trainer) {
      const metrics = METRICS.map((metric) => this.enrichMetric(trainer, metric))
      const missing = metrics.filter((metric) => !metric.hasTarget)
      if (missing.length === metrics.length) return "Noch keine Monatsziele hinterlegt"
      const lowest = metrics.filter((metric) => metric.hasTarget).sort((a, b) => a.attainment - b.attainment)[0]
      if (lowest && lowest.attainment < 80) return `${lowest.label}: ${Math.round(lowest.attainment)}% erreicht`
      if (missing.length) return `${missing.length} Ziele noch nicht hinterlegt`
      return "Details prüfen"
    },
    hasAnyGoal(trainer) {
      return METRICS.some((metric) => hasNumber(this.metricTarget(trainer, metric)) && Number(this.metricTarget(trainer, metric)) > 0)
    },
    appointmentActual(trainer) {
      if (hasNumber(trainer?.appointmentsActual)) return Number(trainer.appointmentsActual)
      const legacy = trainer?.trainerGoal?.goals?.appointmentsActual
      return hasNumber(legacy) ? Number(legacy) : 0
    },
    appointmentSource(trainer) {
      if (["manual", "manual_missing", "workcalendar", "mixed", "mixed_missing"].includes(trainer?.appointmentsActualSource)) {
        return trainer.appointmentsActualSource
      }
      return hasNumber(trainer?.trainerGoal?.goals?.appointmentsActual) ? "manual" : "workcalendar"
    },
    appointmentSourceLabel(trainer) {
      return {
        manual: "Historisch fest hinterlegt",
        manual_missing: "Historischer Ist-Wert fehlt",
        workcalendar: "WorkCalendar ab 01.08.2026",
        mixed: "Historisch + WorkCalendar ab 01.08.2026",
        mixed_missing: "Gemischt · historischer Ist-Wert fehlt",
      }[this.appointmentSource(trainer)]
    },
    trainerAppointmentStatuses(trainer) {
      const breakdown = trainer?.appointmentStatusBreakdown || {}
      return [
        { key: "planned", label: "Geplant", value: Number(breakdown.planned || 0), icon: "mdi-calendar-clock-outline", color: "#60a5fa" },
        { key: "checkedin", label: "Eingecheckt", value: Number(breakdown.checkedin || 0), icon: "mdi-login", color: "#f59e0b" },
        { key: "done", label: "Durchgeführt", value: Number(breakdown.done || 0), icon: "mdi-check-circle-outline", color: "#34d399" },
        { key: "noshow", label: "No-Show", value: Number(breakdown.noshow || 0), icon: "mdi-account-off-outline", color: "#fb7185" },
      ]
    },
    ratingLabel(value) {
      return hasNumber(value) ? `${Number(value).toFixed(1)} / 5` : "Keine Daten"
    },
    interactionLocations(trainer) {
      const locations = trainer?.interactions?.locationCounts || {}
      return Object.entries(locations)
        .map(([name, count]) => ({ name: name || "Ohne Angabe", count: Number(count || 0) }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "de"))
    },
    profileImage(trainer) {
      return trainer?.picture ? `/img/profilbilder${trainer.picture}` : "/img/einmalig/imageUploadPlaceholderPFP.jpg"
    },
    studioName(id) {
      const studio = (this.storeStudios || []).find((entry) => String(entry._id) === String(id))
      return studio?.name || studio?.title || "Zusätzliches Team"
    },
    trainerStudioLabel(trainer) {
      const ids = Array.isArray(trainer?.studio) ? trainer.studio : []
      const names = [...new Set(ids.map((id) => this.studioName(id)))]
      return names.length ? names.join(" · ") : "Kein Center hinterlegt"
    },
    formatNumber(value) {
      return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 1 }).format(Number(value || 0))
    },
    selectMetricAndOpenTeam(metricKey) {
      this.selectedMetricKey = metricKey
      this.activeView = "team"
    },
    openTrainerDetails(trainer) {
      this.selectedTrainer = trainer
      this.detailDialog = true
    },
    openTargetDialog(trainer) {
      if (!this.canEditGoals) return
      this.targetTrainer = trainer
      this.originalTargetGoals = { ...(trainer?.trainerGoal?.goals || {}) }
      this.targetDraft = TARGET_FIELDS.reduce((draft, field) => {
        const value = this.originalTargetGoals[field.key]
        this.$set(draft, field.key, hasNumber(value) ? Number(value) : null)
        return draft
      }, {})
      this.targetDialog = true
    },
    async saveTargets() {
      if (!this.targetTrainer || !this.datePickerRange?.[0]) return
      this.savingTargets = true
      try {
        const goals = { ...this.originalTargetGoals, ...this.targetDraft }
        const saved = await api.saveTrainerGoalsForMonth({
          trainer: this.targetTrainer._id,
          month: this.$moment(this.datePickerRange[0]).format("YYYY-MM"),
          goals,
        })
        const index = this.trainers.findIndex((trainer) => String(trainer._id) === String(this.targetTrainer._id))
        if (index >= 0) {
          const updated = { ...this.trainers[index], trainerGoal: saved || { month: this.$moment(this.datePickerRange[0]).format("YYYY-MM"), goals } }
          this.$set(this.trainers, index, updated)
          TEAM_GOALS_CACHE.set(this.requestKey, { createdAt: Date.now(), trainers: this.trainers.map((trainer) => ({ ...trainer })) })
          this.targetTrainer = updated
          if (this.selectedTrainer && String(this.selectedTrainer._id) === String(updated._id)) this.selectedTrainer = updated
        }
        this.targetDialog = false
        if (this.$toast) this.$toast.success("Monatsziele gespeichert")
      } catch (error) {
        if (this.$toast) this.$toast.error(error?.response?.data?.message || "Monatsziele konnten nicht gespeichert werden")
      } finally {
        this.savingTargets = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.team-goals {
  --surface: #171b24;
  --surface-soft: #1d2330;
  --surface-raised: #222936;
  --border: rgba(148, 163, 184, 0.16);
  --text: #f8fafc;
  --muted: #94a3b8;
  position: relative;
  min-height: 100vh;
  padding: 22px;
  color: var(--text);
  background: radial-gradient(circle at 92% 2%, rgba(56, 189, 248, 0.12), transparent 27rem),
    radial-gradient(circle at 4% 38%, rgba(52, 211, 153, 0.08), transparent 24rem), #10131a;
}

.team-goals__hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 28px;
  padding: 28px 30px;
  border: 1px solid var(--border);
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.96), rgba(17, 24, 39, 0.96));
  box-shadow: 0 24px 60px rgba(2, 6, 23, 0.32);
}

.hero-copy h1,
.panel-heading h2,
.detail-hero h2,
.target-card h2 {
  margin: 0;
  letter-spacing: -0.035em;
}

.hero-copy h1 {
  margin-top: 8px;
  font-size: clamp(2rem, 4vw, 3.25rem);
  line-height: 1;
}
.hero-copy > p {
  max-width: 720px;
  margin: 12px 0 17px;
  color: #cbd5e1;
  font-size: 1rem;
}
.hero-badges,
.hero-meta,
.hero-actions,
.trainer-cell,
.row-actions,
.legend {
  display: flex;
  align-items: center;
}
.hero-badges {
  gap: 10px;
}
.pilot-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(135deg, #2563eb, #0ea5e9);
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.12em;
}
.hero-context {
  color: #93c5fd;
  font-size: 0.75rem;
  font-weight: 700;
}
.hero-meta {
  flex-wrap: wrap;
  gap: 10px 18px;
  color: var(--muted);
  font-size: 0.8rem;
}
.hero-meta span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.hero-refresh {
  color: #e0f2fe !important;
  border: 1px solid rgba(125, 211, 252, 0.3) !important;
  background: rgba(14, 165, 233, 0.12) !important;
  text-transform: none;
}

.pilot-notice {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 14px 0 18px;
  padding: 13px 16px;
  border: 1px solid rgba(96, 165, 250, 0.2);
  border-radius: 15px;
  background: rgba(30, 64, 175, 0.09);
}
.pilot-notice div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.pilot-notice strong {
  font-size: 0.83rem;
}
.pilot-notice span {
  color: #aebdd0;
  font-size: 0.76rem;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}
.kpi-card {
  min-height: 156px;
  padding: 18px;
  border: 1px solid var(--border);
  border-radius: 20px;
  background: linear-gradient(160deg, rgba(34, 41, 54, 0.96), rgba(24, 29, 39, 0.96));
  box-shadow: inset 0 3px 0 var(--kpi-color);
}
.kpi-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 17px;
}
.kpi-icon {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.09);
}
.kpi-badge,
.selection-chip {
  padding: 5px 8px;
  border-radius: 999px;
  color: #a7f3d0;
  background: rgba(16, 185, 129, 0.11);
  font-size: 0.68rem;
  font-weight: 800;
}
.kpi-card > strong {
  display: block;
  font-size: 2rem;
  line-height: 1;
}
.kpi-card > span {
  display: block;
  margin-top: 8px;
  font-weight: 800;
}
.kpi-card > small {
  display: block;
  margin-top: 4px;
  color: var(--muted);
}

.view-switch {
  display: inline-flex;
  gap: 5px;
  padding: 5px;
  border: 1px solid var(--border);
  border-radius: 15px;
  background: rgba(15, 23, 42, 0.7);
}
.view-switch button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border: 0;
  border-radius: 11px;
  color: var(--muted);
  background: transparent;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;
  transition: 0.18s ease;
}
.view-switch button:hover {
  color: #e2e8f0;
  background: rgba(148, 163, 184, 0.08);
}
.view-switch__button--active {
  color: #fff !important;
  background: linear-gradient(135deg, #2563eb, #0284c7) !important;
  box-shadow: 0 8px 18px rgba(2, 132, 199, 0.25);
}

.filter-bar {
  display: grid;
  grid-template-columns: minmax(240px, 1fr) minmax(210px, 320px) auto;
  align-items: center;
  gap: 12px;
  margin: 14px 0;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 17px;
  background: rgba(23, 27, 36, 0.82);
}
.filter-search,
.filter-metric {
  margin: 0 !important;
}
.view-content {
  animation: view-in 0.25s ease-out;
}
@keyframes view-in {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.overview-grid,
.analysis-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(310px, 0.75fr);
  gap: 14px;
}
.panel {
  overflow: hidden;
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 20px;
  background: rgba(23, 27, 36, 0.94);
  box-shadow: 0 16px 38px rgba(2, 6, 23, 0.18);
}
.panel--wide {
  grid-row: span 2;
}
.panel--table {
  padding: 0;
}
.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}
.panel-heading--table {
  padding: 22px 22px 0;
}
.panel-heading h2 {
  margin-top: 3px;
  font-size: 1.25rem;
}
.panel-heading p {
  margin: 5px 0 0;
  color: var(--muted);
  font-size: 0.78rem;
}
.eyebrow {
  color: #34d399;
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 0.13em;
}
.eyebrow--warning {
  color: #fbbf24;
}
.eyebrow--blue {
  color: #60a5fa;
}

.metric-summary-list {
  display: grid;
  gap: 8px;
}
.metric-summary {
  display: grid;
  grid-template-columns: 38px minmax(170px, 1fr) minmax(170px, 0.75fr) 18px;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid transparent;
  border-radius: 14px;
  color: inherit;
  background: rgba(148, 163, 184, 0.045);
  text-align: left;
  cursor: pointer;
  transition: 0.18s ease;
}
.metric-summary:hover {
  border-color: rgba(96, 165, 250, 0.24);
  background: rgba(59, 130, 246, 0.07);
  transform: translateX(2px);
}
.metric-summary__icon {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 11px;
}
.metric-summary__copy {
  display: flex;
  flex-direction: column;
}
.metric-summary__copy small {
  margin-top: 2px;
  color: var(--muted);
}
.metric-summary__progress {
  display: grid;
  grid-template-columns: 1fr 46px;
  align-items: center;
  gap: 9px;
}
.metric-summary__progress > span,
.table-progress > span,
.membership-bar,
.analysis-metrics button > i,
.detail-progress {
  overflow: hidden;
  height: 6px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.13);
}
.metric-summary__progress i,
.membership-bar i,
.analysis-metrics button > i b,
.detail-progress i {
  display: block;
  height: 100%;
  border-radius: inherit;
}
.metric-summary__progress strong {
  font-size: 0.75rem;
  text-align: right;
}

.attention-list {
  display: grid;
  gap: 7px;
}
.attention-person {
  display: grid;
  grid-template-columns: 36px 1fr 18px;
  align-items: center;
  gap: 10px;
  padding: 9px;
  border: 0;
  border-radius: 12px;
  color: inherit;
  background: rgba(148, 163, 184, 0.045);
  text-align: left;
  cursor: pointer;
}
.attention-person:hover {
  background: rgba(245, 158, 11, 0.08);
}
.attention-person span:nth-child(2) {
  display: flex;
  min-width: 0;
  flex-direction: column;
}
.attention-person small {
  overflow: hidden;
  color: var(--muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.person-avatar {
  display: block;
  overflow: hidden;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border: 2px solid rgba(148, 163, 184, 0.22);
  border-radius: 14px;
  background: #334155;
}
.person-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.person-avatar--small {
  width: 36px;
  height: 36px;
  border-radius: 11px;
}
.person-avatar--large {
  width: 70px;
  height: 70px;
  flex-basis: 70px;
  border-radius: 21px;
}
.panel-empty {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 25px 10px;
  text-align: center;
}
.panel-empty strong {
  margin-top: 8px;
}
.panel-empty span {
  max-width: 260px;
  margin-top: 3px;
  color: var(--muted);
  font-size: 0.76rem;
}

.source-total {
  margin: 5px 0 17px;
}
.source-total strong {
  display: block;
  font-size: 2.35rem;
  line-height: 1;
}
.source-total span {
  color: var(--muted);
  font-size: 0.76rem;
}
.source-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 7px;
}
.source-list > div {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px;
  border-radius: 11px;
  background: rgba(148, 163, 184, 0.05);
}
.source-list span:nth-child(2) {
  display: flex;
  flex-direction: column;
  color: var(--muted);
  font-size: 0.67rem;
}
.source-list strong {
  color: #fff;
  font-size: 0.95rem;
}
.source-dot {
  width: 7px;
  height: 7px;
  flex: 0 0 7px;
  border-radius: 50%;
}
.status-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 7px;
  margin-top: 14px;
}
.status-strip div {
  padding: 9px 5px;
  border: 1px solid var(--border);
  border-radius: 11px;
  text-align: center;
}
.status-strip strong,
.status-strip span {
  display: block;
}
.status-strip span {
  margin-top: 2px;
  color: var(--muted);
  font-size: 0.62rem;
}
.source-footnote {
  display: block;
  margin-top: 12px;
  color: #7f8da3;
  line-height: 1.45;
}

.legend {
  flex-wrap: wrap;
  gap: 12px;
  color: var(--muted);
  font-size: 0.7rem;
}
.legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.legend-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
.legend-dot--good {
  background: #34d399;
}
.legend-dot--watch {
  background: #fbbf24;
}
.legend-dot--attention {
  background: #fb7185;
}
.trainer-cell {
  min-width: 225px;
  gap: 10px;
  padding: 4px 0;
}
.trainer-cell > span:nth-child(2) {
  display: flex;
  flex-direction: column;
}
.trainer-cell small {
  max-width: 245px;
  overflow: hidden;
  color: var(--muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.table-number {
  font-size: 1rem;
}
.table-target {
  color: #cbd5e1;
  font-size: 0.78rem;
}
.table-progress {
  display: grid;
  grid-template-columns: minmax(95px, 1fr) 46px;
  align-items: center;
  gap: 8px;
}
.table-progress i {
  display: block;
  height: 100%;
  border-radius: 999px;
}
.table-progress strong {
  font-size: 0.72rem;
  text-align: right;
}
.tone--good {
  background: #34d399 !important;
}
.tone--watch {
  background: #fbbf24 !important;
}
.tone--attention {
  background: #fb7185 !important;
}
.tone--missing {
  background: #64748b !important;
}
.appointment-cell {
  display: flex;
  flex-direction: column;
}
.appointment-cell small {
  font-size: 0.64rem;
}
.source-text--workcalendar {
  color: #7dd3fc;
}
.source-text--manual {
  color: #c4b5fd;
}
.source-text--mixed {
  color: #fbbf24;
}
.source-text--manual_missing,
.source-text--mixed_missing {
  color: #fda4af;
}
.status-pill {
  display: inline-flex;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 800;
}
.status-pill--good {
  color: #a7f3d0;
  background: rgba(16, 185, 129, 0.12);
}
.status-pill--watch {
  color: #fde68a;
  background: rgba(245, 158, 11, 0.12);
}
.status-pill--attention {
  color: #fecdd3;
  background: rgba(244, 63, 94, 0.12);
}
.status-pill--missing {
  color: #cbd5e1;
  background: rgba(100, 116, 139, 0.15);
}
.row-actions {
  justify-content: flex-end;
  gap: 3px;
}
.team-table {
  background: transparent !important;
}
::v-deep .team-table .v-data-table__wrapper {
  border-top: 1px solid var(--border);
}
::v-deep .team-table tbody tr:hover {
  background: rgba(59, 130, 246, 0.055) !important;
}
::v-deep .team-table th {
  color: #94a3b8 !important;
  font-size: 0.67rem !important;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.analysis-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.analysis-metrics button {
  display: flex;
  min-height: 140px;
  flex-direction: column;
  padding: 15px;
  border: 1px solid var(--border);
  border-radius: 15px;
  color: inherit;
  background: rgba(148, 163, 184, 0.045);
  text-align: left;
  cursor: pointer;
}
.analysis-metrics button:hover {
  border-color: rgba(96, 165, 250, 0.28);
}
.analysis-metrics button > span {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #cbd5e1;
  font-size: 0.75rem;
  font-weight: 700;
}
.analysis-metrics button > strong {
  margin-top: 12px;
  font-size: 1.65rem;
}
.analysis-metrics button > small {
  color: var(--muted);
}
.analysis-metrics button > i {
  width: 100%;
  margin-top: auto;
}
.center-memberships {
  display: grid;
  gap: 14px;
}
.center-memberships > div {
  display: grid;
  gap: 7px;
}
.center-memberships span:first-child {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.center-memberships small {
  color: var(--muted);
}
.membership-bar i {
  background: linear-gradient(90deg, #0ea5e9, #38bdf8);
}
.quality-ring {
  position: relative;
  display: grid;
  width: 150px;
  height: 150px;
  margin: 10px auto 14px;
  place-items: center;
  border-radius: 50%;
  background: conic-gradient(#34d399 var(--quality), rgba(148, 163, 184, 0.12) 0);
}
.quality-ring::before {
  width: 118px;
  height: 118px;
  border-radius: 50%;
  background: #171b24;
  content: "";
}
.quality-ring > div {
  position: absolute;
  display: flex;
  align-items: center;
  flex-direction: column;
}
.quality-ring strong {
  font-size: 1.75rem;
}
.quality-ring span {
  color: var(--muted);
  font-size: 0.68rem;
}
.quality-copy {
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
}
.quality-copy span {
  max-width: 240px;
  margin-top: 3px;
  color: var(--muted);
  font-size: 0.72rem;
}

.detail-card,
.target-card {
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.19) !important;
  border-radius: 22px !important;
  background: #151a23 !important;
}
.detail-hero {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 22px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(130deg, rgba(30, 41, 59, 0.95), rgba(15, 23, 42, 0.95));
}
.detail-hero p,
.target-card .v-card__title p {
  margin: 5px 0 0;
  color: var(--muted);
  font-size: 0.77rem;
}
.detail-body {
  padding: 22px !important;
}
.detail-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.detail-metrics article {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 13px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: rgba(148, 163, 184, 0.045);
}
.detail-metric__icon {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 11px;
}
.detail-metrics article > div {
  display: flex;
  flex-direction: column;
}
.detail-metrics article small {
  color: var(--muted);
}
.detail-metrics article strong {
  font-size: 1rem;
}
.detail-metrics article em {
  color: #94a3b8;
  font-size: 0.76rem;
  font-style: normal;
  font-weight: 500;
}
.detail-metrics article > b {
  color: #cbd5e1;
  font-size: 0.68rem;
}
.detail-progress {
  grid-column: 2 / -1;
  width: 100%;
}
.detail-insights {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 15px;
}
.detail-insights article {
  padding: 15px;
  border: 1px solid var(--border);
  border-radius: 15px;
  background: rgba(148, 163, 184, 0.04);
}
.detail-insights h3 {
  margin: 3px 0 12px;
  font-size: 1rem;
}
.detail-insights__wide {
  grid-column: 1 / -1;
}
.rating-row {
  display: grid;
  grid-template-columns: minmax(105px, 1fr) auto 68px;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  border-top: 1px solid rgba(148, 163, 184, 0.08);
}
.rating-row > span {
  color: #cbd5e1;
  font-size: 0.72rem;
}
.rating-row > strong {
  color: var(--muted);
  font-size: 0.66rem;
  text-align: right;
}
.detail-facts {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
.detail-facts div {
  padding: 9px;
  border-radius: 11px;
  background: rgba(148, 163, 184, 0.055);
}
.detail-facts strong,
.detail-facts span {
  display: block;
}
.detail-facts strong {
  font-size: 1rem;
}
.detail-facts span {
  margin-top: 2px;
  color: var(--muted);
  font-size: 0.63rem;
}
.location-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.location-chips > span {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 9px;
  border-radius: 999px;
  color: #cbd5e1;
  background: rgba(245, 158, 11, 0.08);
  font-size: 0.69rem;
}
.location-chips strong {
  color: #fde68a;
}
.appointment-detail {
  margin-top: 15px;
  padding: 17px;
  border: 1px solid rgba(96, 165, 250, 0.18);
  border-radius: 16px;
  background: rgba(30, 64, 175, 0.07);
}
.appointment-detail__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}
.appointment-detail h3 {
  margin: 3px 0 0;
}
.source-pill {
  padding: 6px 9px;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 800;
}
.source-pill--workcalendar {
  color: #bae6fd;
  background: rgba(14, 165, 233, 0.13);
}
.source-pill--manual {
  color: #ddd6fe;
  background: rgba(139, 92, 246, 0.13);
}
.source-pill--mixed {
  color: #fde68a;
  background: rgba(245, 158, 11, 0.13);
}
.source-pill--manual_missing,
.source-pill--mixed_missing {
  color: #fecdd3;
  background: rgba(244, 63, 94, 0.14);
}
.appointment-detail__statuses {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 14px;
}
.appointment-detail__statuses div {
  display: grid;
  padding: 10px;
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.055);
  text-align: center;
}
.appointment-detail__statuses strong {
  margin-top: 4px;
  font-size: 1.1rem;
}
.appointment-detail__statuses span {
  color: var(--muted);
  font-size: 0.64rem;
}
.appointment-detail > p {
  margin: 12px 0 0;
  color: #94a3b8;
  font-size: 0.72rem;
}

.target-card .v-card__title {
  padding: 22px;
  border-bottom: 1px solid var(--border);
}
.target-card .v-card__text {
  padding: 20px 22px !important;
}
.target-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 12px;
}
.actual-readonly {
  display: flex;
  gap: 13px;
  padding: 14px;
  border: 1px solid rgba(96, 165, 250, 0.2);
  border-radius: 14px;
  background: rgba(37, 99, 235, 0.08);
}
.actual-readonly > span {
  display: grid;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  place-items: center;
  border-radius: 11px;
  background: rgba(59, 130, 246, 0.12);
}
.actual-readonly div {
  display: grid;
}
.actual-readonly small {
  color: #93c5fd;
}
.actual-readonly strong {
  font-size: 1.45rem;
}
.actual-readonly p {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 0.7rem;
}

.loading-shell {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}
.skeleton-card,
.skeleton-panel {
  border-radius: 20px;
  background: linear-gradient(90deg, #1b2230 25%, #252e3d 50%, #1b2230 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.skeleton-card {
  height: 156px;
}
.skeleton-panel {
  grid-column: 1 / -1;
  height: 430px;
}
@keyframes shimmer {
  to {
    background-position: -200% 0;
  }
}
.empty-state {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 18px;
  padding: 70px 20px;
  border: 1px solid var(--border);
  border-radius: 22px;
  background: var(--surface);
  text-align: center;
}
.empty-state h2 {
  margin: 12px 0 4px;
}
.empty-state p {
  max-width: 520px;
  color: var(--muted);
}
.refresh-overlay {
  position: fixed;
  z-index: 20;
  right: 25px;
  bottom: 25px;
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  border: 1px solid var(--border);
  border-radius: 50%;
  background: #171b24;
  box-shadow: 0 14px 34px rgba(2, 6, 23, 0.45);
}

@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .overview-grid,
  .analysis-grid {
    grid-template-columns: 1fr;
  }
  .panel--wide {
    grid-row: auto;
  }
}

@media (max-width: 760px) {
  .team-goals {
    padding: 10px;
  }
  .team-goals__hero {
    align-items: flex-start;
    flex-direction: column;
    padding: 21px;
    border-radius: 20px;
  }
  .hero-actions,
  .hero-refresh {
    width: 100%;
  }
  .kpi-grid {
    grid-template-columns: 1fr 1fr;
    gap: 9px;
  }
  .kpi-card {
    min-height: 138px;
    padding: 14px;
  }
  .kpi-card > strong {
    font-size: 1.55rem;
  }
  .view-switch {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    width: 100%;
  }
  .view-switch button {
    justify-content: center;
    padding: 9px 7px;
  }
  .filter-bar {
    grid-template-columns: 1fr;
  }
  .metric-summary {
    grid-template-columns: 36px 1fr 18px;
  }
  .metric-summary__progress {
    grid-column: 2 / 3;
    grid-row: 2;
  }
  .analysis-metrics,
  .detail-metrics,
  .detail-insights,
  .target-form {
    grid-template-columns: 1fr;
  }
  .detail-insights__wide {
    grid-column: auto;
  }
  .rating-row {
    grid-template-columns: 1fr;
  }
  .rating-row > strong {
    text-align: left;
  }
  .source-list {
    grid-template-columns: 1fr;
  }
  .status-strip,
  .appointment-detail__statuses {
    grid-template-columns: repeat(2, 1fr);
  }
  .detail-hero {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .detail-hero .v-spacer {
    display: none;
  }
  .detail-hero .v-btn:not(.v-btn--icon) {
    order: 4;
    width: 100%;
  }
}
</style>
